import * as React from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import AppAppBar from "../components/AppAppBar";
import getLPTheme from "./getLPTheme";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Footer from "../components/Footer";
import { Fade, Slide, Typography, Chip } from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SettingsIcon from "@mui/icons-material/Settings";
import ReceiptIcon from "@mui/icons-material/Receipt";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";

// Import company-specific components
import CompanyRoomBookings from "../components/CompanyDashboard/CompanyRoomBookings";
import CompanyUsers from "../components/CompanyDashboard/CompanyUsers";
import CompanySettings from "../components/CompanyDashboard/CompanySettings";

/**
 * Company Admin Dashboard Component - Dashboard interface for Company Admins
 *
 * Features company-specific management with animated tab navigation:
 * - Company Room Bookings (filtered by company)
 * - Team Management (company users only)
 * - Company Settings (company profile and configuration)
 * - Invoices (Coming Soon)
 *
 * @component
 * @example
 * <CompanyAdminDashboard />
 */
export default function CompanyAdminDashboard() {
  const [mode, setMode] = React.useState("light");
  const LPtheme = createTheme(getLPTheme(mode));
  const [value, setValue] = React.useState(0);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const authUser = useAuthUser();

  // Debug the authUser object
  React.useEffect(() => {
    console.log("CompanyAdminDashboard - authUser:", authUser);
    console.log(
      "CompanyAdminDashboard - authUser?.companyId:",
      authUser?.companyId
    );
  }, [authUser]);

  // Load the selected tab value from localStorage on component mount
  React.useEffect(() => {
    const storedValue = localStorage.getItem("companyAdminSelectedTab");
    if (storedValue !== null) {
      setValue(parseInt(storedValue, 10));
    }
  }, []);

  /**
   * Toggle between light and dark color modes
   */
  const toggleColorMode = () => {
    setMode((prev) => (prev === "dark" ? "light" : "dark"));
  };

  /**
   * Handle tab change with smooth transition animation
   * Implements a brief transition state to ensure smooth content switching
   *
   * @param {Event} event - The tab change event
   * @param {number} newValue - The index of the newly selected tab
   */
  const handleChange = (event, newValue) => {
    if (newValue === value) return; // Prevent unnecessary transitions

    // Start transition animation
    setIsTransitioning(true);

    // Brief delay to allow fade out animation
    setTimeout(() => {
      // Store the selected tab value in localStorage for persistence
      localStorage.setItem("companyAdminSelectedTab", newValue);
      setValue(newValue);
      setIsTransitioning(false);
    }, 150); // 150ms delay for smooth transition
  };

  /**
   * Animated Tab Panel Component
   * Wraps content with fade and slide animations for smooth transitions
   *
   * @param {Object} props - Component props
   * @param {React.ReactNode} props.children - Content to animate
   * @param {number} props.index - Tab index for this panel
   * @param {number} props.value - Currently active tab index
   * @param {string} [props.direction='left'] - Slide animation direction
   */
  const AnimatedTabPanel = ({ children, index, value, direction = "left" }) => {
    const isActive = value === index && !isTransitioning;

    return (
      <Slide
        direction={direction}
        in={isActive}
        timeout={{
          enter: 400,
          exit: 200,
        }}
        mountOnEnter
        unmountOnExit
      >
        <div>
          <Fade
            in={isActive}
            timeout={{
              enter: 500,
              exit: 150,
            }}
          >
            <Box
              sx={{
                opacity: isActive ? 1 : 0,
                transform: isActive ? "translateY(0)" : "translateY(20px)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              {children}
            </Box>
          </Fade>
        </div>
      </Slide>
    );
  };

  return (
    <ThemeProvider theme={LPtheme}>
      <CssBaseline />
      <AppAppBar
        mode={mode}
        toggleColorMode={toggleColorMode}
      />
      <Box
        sx={{
          backgroundImage:
            "linear-gradient(180deg, rgba(203, 224, 15, 0.2), #fff)",
          backgroundSize: "100% 200px",
          backgroundRepeat: "no-repeat",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: { xs: 14, sm: 20 },
        }}
      >
        {/* Enhanced Tabs with company-specific styling and animations */}
        <Slide
          direction='down'
          in={true}
          timeout={600}
        >
          <Tabs
            variant='scrollable'
            value={value}
            onChange={handleChange}
            aria-label='Company Dashboard navigation tabs'
            scrollButtons
            allowScrollButtonsMobile
            sx={{
              mb: 8,
              width: { xs: "100%", sm: "auto", md: "auto", xl: "auto" },
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
                minHeight: 64,
                minWidth: 120,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  color: "primary.main",
                  transform: "translateY(-2px)",
                  "& .MuiSvgIcon-root": {
                    transform: "scale(1.1)",
                  },
                },
                "&.Mui-selected": {
                  color: "primary.main",
                  fontWeight: 700,
                  "& .MuiSvgIcon-root": {
                    color: "primary.main",
                  },
                },
                "& .MuiSvgIcon-root": {
                  fontSize: "1.5rem",
                  marginBottom: "4px",
                  transition: "all 0.2s ease-in-out",
                },
              },
            }}
          >
            <Tab
              icon={<CalendarTodayIcon />}
              label='Company Bookings'
              iconPosition='top'
              aria-label='Company room bookings and calendar management'
            />
            <Tab
              icon={<PeopleIcon />}
              label='Team Management'
              iconPosition='top'
              aria-label='Manage company team members'
            />
            <Tab
              icon={<SettingsIcon />}
              label='Company Settings'
              iconPosition='top'
              aria-label='Company profile and settings'
            />
            <Tab
              icon={<ReceiptIcon />}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Invoices
                  <Chip
                    label='Coming Soon'
                    size='small'
                    sx={{
                      fontSize: "0.7rem",
                      height: "20px",
                      backgroundColor: "grey.300",
                      color: "grey.600",
                    }}
                  />
                </Box>
              }
              iconPosition='top'
              aria-label='Invoices (Coming Soon)'
              disabled
              sx={{
                "&.Mui-disabled": {
                  opacity: 0.6,
                  "& .MuiSvgIcon-root": {
                    color: "grey.400",
                  },
                  "&:hover": {
                    transform: "none",
                    "& .MuiSvgIcon-root": {
                      transform: "none",
                    },
                  },
                },
              }}
            />
          </Tabs>
        </Slide>

        <Container
          maxWidth='lg'
          sx={{
            mb: 4,
            position: "relative",
            minHeight: "60vh", // Prevent layout shift during transitions
          }}
        >
          {/* Company Room Bookings Tab Panel */}
          <AnimatedTabPanel
            value={value}
            index={0}
            direction='right'
          >
            <CompanyRoomBookings companyId={authUser?.companyId} />
          </AnimatedTabPanel>

          {/* Team Management Tab Panel */}
          <AnimatedTabPanel
            value={value}
            index={1}
            direction='up'
          >
            <CompanyUsers companyId={authUser?.companyId} />
          </AnimatedTabPanel>

          {/* Company Settings Tab Panel */}
          <AnimatedTabPanel
            value={value}
            index={2}
            direction='down'
          >
            <CompanySettings companyId={authUser?.companyId} />
          </AnimatedTabPanel>

          {/* Invoices Tab Panel - Coming Soon */}
          <AnimatedTabPanel
            value={value}
            index={3}
            direction='up'
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "400px",
                textAlign: "center",
                py: 8,
              }}
            >
              <ReceiptIcon
                sx={{
                  fontSize: "4rem",
                  color: "grey.400",
                  mb: 3,
                }}
              />
              <Typography
                variant='h4'
                sx={{
                  color: "grey.600",
                  mb: 2,
                  fontWeight: 600,
                }}
              >
                Company Invoices
              </Typography>
              <Chip
                label='Coming Soon'
                sx={{
                  fontSize: "1rem",
                  px: 2,
                  py: 1,
                  backgroundColor: "primary.light",
                  color: "primary.contrastText",
                  mb: 3,
                }}
              />
              <Typography
                variant='body1'
                sx={{
                  color: "grey.500",
                  maxWidth: "500px",
                  lineHeight: 1.6,
                }}
              >
                We're working hard to bring you a comprehensive invoicing system
                for your company. This feature will allow you to generate,
                manage, and track invoices for your company's room bookings and
                events.
              </Typography>
            </Box>
          </AnimatedTabPanel>

          {/* Loading overlay during transitions */}
          <Fade in={isTransitioning}>
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                backdropFilter: "blur(2px)",
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  border: "3px solid",
                  borderColor: "primary.main",
                  borderTopColor: "transparent",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  "@keyframes spin": {
                    "0%": {
                      transform: "rotate(0deg)",
                    },
                    "100%": {
                      transform: "rotate(360deg)",
                    },
                  },
                }}
              />
            </Box>
          </Fade>
        </Container>

        <Footer />
      </Box>
    </ThemeProvider>
  );
}
